'use client'

import React, { useRef, useState, useEffect, useLayoutEffect, createContext } from 'react'
import { motion, useMotionValue, animate } from 'framer-motion'
import styles from './CardSlider.module.css'
import { slideInVariants } from '@/lib/animations'
import { useBreakpoint } from '@/hooks/useBreakpoints'

const DragDistanceContext = createContext<React.RefObject<number> | null>(null)

interface CardSliderProps {
    children: React.ReactNode
    gap?: number
    startX?: number
    endGap?: number
    isBlurVariant?: boolean
    useTranslateForStartX?: boolean
    /**
     * Where the carousel sits on first load.
     * `'end'` shows the first cards, not the last card.
     * `'start'` leaves the default track position (does not force the first-card view).
     */
    initialPosition?: 'start' | 'end'
}

export function CardSlider({
    children,
    gap = 24,
    startX = 0,
    endGap = 0,
    isBlurVariant = false,
    useTranslateForStartX = false,
    initialPosition = 'start',
}: CardSliderProps) {
    const containerRef = useRef<HTMLDivElement>(null)
    const contentRef = useRef<HTMLDivElement>(null)
    const { isSm } = useBreakpoint()

    const effectiveEndGap = isSm ? 32 : endGap
    const effectiveStartX = useTranslateForStartX ? startX : isSm && startX > 0 ? 32 : startX

    const x = useMotionValue(useTranslateForStartX ? effectiveStartX : 0)
    const [dragConstraints, setDragConstraints] = useState({ left: 0, right: 0 })
    const dragStartPositionRef = useRef(0)
    const dragDistanceRef = useRef(0)
    const velocityRef = useRef(0)
    // Where we last placed the first cards, so we know if the user has scrolled away.
    const appliedInitialMrRef = useRef<number | null>(null)

    useLayoutEffect(() => {
        if (!containerRef.current || !contentRef.current) return

        const container = containerRef.current
        const content = contentRef.current
        const mr = useTranslateForStartX ? effectiveStartX : 0

        const update = () => {
            const cw = container.offsetWidth
            const sw = content.scrollWidth

            let leftConstraint = mr

            if (useTranslateForStartX) {
                const visibleWidth = cw - mr
                const overflowAmount = sw - visibleWidth
                const hasMeaningfulOverflow = overflowAmount > 40

                if (hasMeaningfulOverflow) {
                    leftConstraint = cw - sw
                }
            } else {
                const overflowAmount = sw - cw
                const hasMeaningfulOverflow = overflowAmount > 40
                if (hasMeaningfulOverflow) {
                    leftConstraint = Math.min(-overflowAmount, 0)
                }
            }

            setDragConstraints({ left: leftConstraint, right: mr })

            // Show the first cards on load. If the user has already scrolled,
            // do not jump them back when the layout size changes.
            if (initialPosition === 'end') {
                const stillAtStart =
                    appliedInitialMrRef.current === null || x.get() === appliedInitialMrRef.current
                if (stillAtStart) {
                    x.set(mr)
                    appliedInitialMrRef.current = mr
                }
            }
        }

        update()
        const ro = new ResizeObserver(update)
        ro.observe(content)
        const t = setTimeout(update, 100)
        window.addEventListener('resize', update)
        return () => {
            ro.disconnect()
            clearTimeout(t)
            window.removeEventListener('resize', update)
        }
    }, [effectiveStartX, initialPosition, useTranslateForStartX, x])

    useEffect(() => {
        const container = containerRef.current
        if (!container) return

        const handleWheel = (e: WheelEvent) => {
            const absDeltaX = Math.abs(e.deltaX)
            const absDeltaY = Math.abs(e.deltaY)

            const isPrimarilyHorizontal = absDeltaX > absDeltaY
            const isShiftScroll = e.shiftKey && absDeltaY > 0

            if (absDeltaX > 0 || isShiftScroll) {
                e.preventDefault()
                e.stopPropagation()
                e.stopImmediatePropagation()
            }

            if (isPrimarilyHorizontal || isShiftScroll) {
                const delta = isPrimarilyHorizontal ? e.deltaX : e.deltaY
                const currentX = x.get()
                const newX = currentX - delta

                const constrainedX = Math.max(dragConstraints.left, Math.min(dragConstraints.right, newX))

                x.set(constrainedX)
            }
        }

        container.addEventListener('wheel', handleWheel, { passive: false, capture: true })
        return () => {
            container.removeEventListener('wheel', handleWheel, { capture: true } as any)
        }
    }, [x, dragConstraints])

    const handleDragStart = (event: any, info: any) => {
        dragStartPositionRef.current = x.get()
        dragDistanceRef.current = 0
        velocityRef.current = 0
    }

    const handleDrag = (event: any, info: any) => {
        const currentX = x.get()
        dragDistanceRef.current = Math.abs(currentX - dragStartPositionRef.current)
        velocityRef.current = info.velocity.x
    }

    const handleDragEnd = (event: any, info: any) => {
        if (!containerRef.current || !contentRef.current) return

        const currentX = x.get()
        const velocity = info.velocity.x || velocityRef.current
        const momentumFactor = 0.4
        const velocityOffset = velocity * momentumFactor
        const targetXWithMomentum = currentX + velocityOffset

        const targetX = Math.max(dragConstraints.left, Math.min(dragConstraints.right, targetXWithMomentum))

        const hasMomentum = Math.abs(velocity) > 50

        animate(x, targetX, {
            type: 'spring',
            stiffness: hasMomentum ? 120 : 200,
            damping: hasMomentum ? 20 : 35,
            mass: hasMomentum ? 0.6 : 1.2,
            velocity: hasMomentum ? velocity * 0.6 : 0,
        })
        dragDistanceRef.current = 0
        velocityRef.current = 0
    }

    const content = <DragDistanceContext.Provider value={dragDistanceRef}>{children}</DragDistanceContext.Provider>

    const containerVariants = isBlurVariant ? {} : slideInVariants

    return (
        <div
            ref={containerRef}
            className={`${styles.container}`}
            /* Exposed so cards sizing themselves against the slider can subtract the gap. */
            style={{ '--card-gap': `${gap}px` } as React.CSSProperties}
        >
            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-80px 0px' }}
                variants={containerVariants}
            >
                <motion.div
                    ref={contentRef}
                    drag="x"
                    dragConstraints={dragConstraints}
                    dragElastic={0.1}
                    dragMomentum={false}
                    dragDirectionLock={true}
                    dragPropagation={false}
                    onDragStart={handleDragStart}
                    onDrag={handleDrag}
                    onDragEnd={handleDragEnd}
                    style={{
                        x,
                        gap: `${gap}px`,
                        paddingLeft: useTranslateForStartX ? 0 : `${effectiveStartX}px`,
                    }}
                    className={styles.content}
                >
                    {content}
                    <div style={{ minWidth: effectiveEndGap }} />
                </motion.div>
            </motion.div>
        </div>
    )
}

export { DragDistanceContext }
